
	import {
		computed,
		defineComponent,
		inject,
		onMounted,
		reactive,
		ref,
	} from 'vue';
	import { MrUiKitButton, MrUiKitForm } from 'mr-ui-kit';
	import { useI18n } from 'vue-i18n';
	import * as validators from '@vuelidate/validators';
	import i18Validators from '@/utils/i18n-validators';
	import customValidator from '@/utils/custom-validator';
	import { useStore } from 'vuex';
	import { useRoute } from 'vue-router';
	import {
		BreadcrumbInstance,
		BreadcrumbStructureItem,
		MultiLanguageField,
		OrganizationInfoForm,
		RouteEntity,
	} from '@/interfaces';
	import OrganizationService from '@/services/OrganizationService';
	import FileService from '@/services/FileService';
	import { currentI18nLanguage } from '@/plugins/i18n';

	export default defineComponent({
		name: 'OrganizationPersonalData',
		components: {
			MrUiKitForm,
			MrUiKitButton,
		},
		setup() {
			const { t, locale } = useI18n();
			const withI18nMessage = validators.createI18nMessage({ t });
			const required = withI18nMessage(validators.required, {
				messagePath: i18Validators.required,
			});
			const multiRequired = withI18nMessage(customValidator.multiRequired, {
				messagePath: i18Validators.multiRequired,
			});
			const store = useStore();
			const entityId = ref<number>(-1);
			const route = useRoute();
			const form = ref<OrganizationInfoForm | null>(null);
			const fields = reactive([
				{
					component: 'MrUiKitToggle',
					name: 'active',
					props: {
						label: computed(() => t('common.fields.deleted')),
						labelPosition: 'left',
						subtitle: computed(() => t('common.fields.deletedSubtitle')),
					},
				},
				{
					component: 'MrUiKitFileUpload',
					name: 'cover',
					props: {
						label: computed(() => t('common.fields.cover')),
					},
				},
				{
					component: 'MrUiKitFileUpload',
					name: 'file',
					props: {
						label: 'Logo',
					},
				},
				{
					component: 'MrUiKitMultiLanguage',
					name: 'fullname',
					props: {
						label: computed(() => t('common.fields.fullname')),
						labelPosition: 'left',
						currentLanguage: currentI18nLanguage,
						languages: store.getters.supportedLanguages.map((l: any) => {
							return {
								value: l.id.toLowerCase(),
								label: l.id.toUpperCase(),
							};
						}),
					},
					rules: {
						multiRequired,
					},
				},
				{
					component: 'MrUiKitInput',
					name: 'website',
					props: {
						label: computed(() => t('common.fields.website')),
						labelPosition: 'left',
					},
				},
				{
					component: 'MrUiKitInput',
					name: 'telephone',
					props: {
						label: computed(() => t('common.fields.telephone')),
						labelPosition: 'left',
					},
				},
				{
					component: 'MrUiKitToggle',
					name: 'publicTelephone',
					props: {
						label: computed(() => t('common.fields.publicTelephone')),
						labelPosition: 'left',
						subtitle: computed(() =>
							t('common.fields.publicTelephoneSubtitle'),
						),
					},
				},
				{
					component: 'MrUiKitInput',
					name: 'email',
					props: {
						label: 'Email',
						labelPosition: 'left',
					}
				},
				{
					component: 'MrUiKitToggle',
					name: 'publicEmail',
					props: {
						label: computed(() => t('common.fields.publicEmail')),
						labelPosition: 'left',
						subtitle: computed(() => t('common.fields.publicEmailSubtitle')),
					},
				},
				{
					component: 'MrUiKitInput',
					name: 'facebook',
					props: {
						label: 'Link Facebook',
						labelPosition: 'left',
					},
				},
				{
					component: 'MrUiKitInput',
					name: 'instagram',
					props: {
						label: 'Link instagram',
						labelPosition: 'left',
					},
				},
				{
					component: 'MrUiKitToggle',
					name: 'isNational',
					props: {
						label: computed(() => t("common.fields.isNational")),
						labelPosition: "left",
					},
				},
				{
					component: 'MrUiKitMultiLanguage',
					name: 'bio',
					props: {
						type: 'texteditor',
						innerProps: {
							toolbarElements: [
								'header',
								'list',
								'fontStyle',
								'indent',
								'colors',
							],
						},
						label: computed(() => t('common.bio')),
						labelPosition: 'left',
						currentLanguage: currentI18nLanguage,
						languages: store.getters.supportedLanguages.map((l: any) => {
							return {
								value: l.id.toLowerCase(),
								label: l.id.toUpperCase(),
							};
						}),
					},
				},
			]);

			onMounted(async () => {
				entityId.value = +route.params.id;
				form.value = await OrganizationService.getMultiLanguageById(
					entityId.value,
				);
				const currentOrganization = await OrganizationService.getById(
					entityId.value,
					locale.value,
				);

				store.commit('setBreadcrumbRebuild');
				store.commit('pushBreadcrumb', {
					path: '/organizations/' + entityId.value + '/edit',
					label:
						form.value.fullname[
							locale.value as keyof MultiLanguageField<string>
						],
					type: BreadcrumbStructureItem.Organization,
					isBackoffice: true,
					entityType: RouteEntity.Organization,
					data: {
						organization: currentOrganization,
					},
				});
			});

			const onUpdateForm = async (event: any) => {
				if (form.value == null) return;
				const field: keyof OrganizationInfoForm = event.item.name;

				switch (event.type) {
					case 'upload':
						const toUpload = form.value[field].file;
						if (toUpload) {
							const uploadedFile = await FileService.upload(
								form.value[field].file,
							);
							form.value[field].url = uploadedFile.url;
							form.value[field].file = uploadedFile;
						}
						break;
					default:
						return;
				}
			};

			const onSubmit = async (disabled: boolean) => {
				if (form.value == null || disabled) return;
				try {
					await OrganizationService.updateById(entityId.value, form.value);
					store.commit('setAlert', {
						type: 'success',
						message: t('messages.generalSuccess.update'),
					});
				} catch (err) {
					console.log(err);
					store.commit('setAlert', {
						type: 'danger',
						message: t('messages.generalError.update'),
					});
				}
			};

			return {
				t,
				onUpdateForm,
				onSubmit,
				form,
				fields,
			};
		},
	});
